/**
 * Single source of truth for all site content.
 *
 * Every sentence here is LIDA's own copy, carried over from the previous site.
 * Headlines and eyebrow labels are the only new writing, and each one is
 * derived from a statement below — no invented facts, figures or claims.
 *
 * To change site text, change it here. Components only render.
 */

export const company = {
  name: 'LIDA Engineering',
  legalTT: 'LIDA Engineering Limited',
  legalGY: 'LIDA Engineering Inc.',
  tagline:
    'Providing superior service to meet the needs of our diverse clientele whilst ensuring that it is delivered on time and within budget.',
  metaDescription:
    'LIDA Engineering — a Construction & Engineering Company based in Trinidad and Tobago, with LIDA Engineering Inc. operating in Guyana with Local Content Certification.',
};

export const contact = {
  address: 'Lot 18A Mohican Way, Aranguez South, San Juan, Trinidad, W.I',
  mapUrl:
    'https://www.google.com/maps/search/?api=1&query=Lot+18A+Mohican+Way,+Aranguez+South,+San+Juan,+Trinidad',
  email: 'admin@lidaengineering.com',
  phones: ['+1 868 256-5432', '+1 868 255-5432'],
  socials: [
    { label: 'Instagram', href: 'https://www.instagram.com/lidaengineering', icon: 'instagram' },
    {
      label: 'Facebook',
      href: 'https://www.facebook.com/profile.php?id=61560380788685',
      icon: 'facebook',
    },
    {
      label: 'LinkedIn',
      href: 'https://www.linkedin.com/company/lida-engineering-ltd/',
      icon: 'linkedin',
    },
  ],
};

export const hero = {
  eyebrow: 'Trinidad & Tobago · Guyana',
  title: 'Construction & engineering, delivered on time and within budget.',
  lead:
    'LIDA Engineering Limited builds in Trinidad and Tobago. LIDA Engineering Inc. operates in Guyana with Local Content Certification.',
  primaryCta: { label: 'Explore our services', href: '#services' },
  secondaryCta: { label: 'Contact us', href: '#contact' },
};

/** Each item traces directly to a sentence in `about.paragraphs`. */
export const credentials = [
  { label: 'Trinidad & Tobago', detail: 'LIDA Engineering Limited' },
  { label: 'Guyana', detail: 'Local Content Certified' },
  { label: 'Fabricators & Contractors', detail: 'Designers and consultants' },
  { label: 'Strategic partnerships', detail: 'Machinery, transport and supplies' },
];

export const about = {
  eyebrow: 'About Us',
  title: 'Construction and engineering across Trinidad, Tobago and Guyana.',
  paragraphs: [
    'LIDA Engineering Limited is a Construction & Engineering Company based in Trinidad and Tobago, while LIDA Engineering Inc. operates in Guyana with Local Content Certification.',
    'We have formed strategic partnerships in the sector, granting us access to heavy machinery, transportation, building supplies, and furnishings.',
    'Our dedication has opened doors as fabricators, general contractors, designers, and consultants. We are ready to meet the challenges ahead.',
  ],
  roles: ['Fabricators', 'General Contractors', 'Designers', 'Consultants'],
};

export const mission = {
  eyebrow: 'Mission & Vision',
  statement: {
    title: 'Mission Statement',
    body:
      'To provide a superior service to meet the needs of our diverse clientele whilst ensuring that it is delivered on time and within budget. To ensure our employees are provided with a safe working environment while pursuing continued excellence in training and development.',
  },
  vision: {
    title: 'Our Vision',
    body:
      'To be a global leader in innovative construction and engineering solutions, delivering sustainable and cutting-edge infrastructure that empowers communities, transforms landscapes, and inspires progress for generations to come.',
  },
};

export const servicesIntro =
  'Our construction management methodology is aimed at exceeding our client’s expectations on every project.';

export const services = [
  {
    id: 'residential-commercial',
    title: 'Residential & Commercial',
    summary:
      'Building attractive, affordable, energy efficient housing on time and within budget.',
    image: require('../images/s-3.jpeg'),
    link: '/ServicesResidentialCommercial',
  },
  {
    id: 'industrial-mechanical',
    title: 'Industrial & Mechanical',
    summary:
      'Providing specialized expertise in design, analysing, and optimizing mechanical systems, processes, and products.',
    image: require('../images/s-2.jpeg'),
    link: '/ServicesIndustrialMechanical',
  },
  {
    id: 'management',
    title: 'Management Services',
    summary:
      'We manage various phases of projects as an extension of the owner’s organization.',
    image: require('../images/s-1.png'),
    link: '/ServicesManagement',
  },
];

/** The full capability list. Rendered in full on the Residential & Commercial page. */
export const capabilities = [
  {
    title: 'Pre-Project Planning',
    description:
      'Develop detailed timelines, budgets, and resource strategies to establish a strong foundation for successful project delivery.',
  },
  {
    title: 'Pre-Construction Services',
    description:
      'Conduct feasibility studies, design coordination, and site assessments to prepare thoroughly before construction begins.',
  },
  {
    title: 'Construction Management',
    description:
      'Oversee all construction activities daily to ensure safety, quality workmanship, and timely completion according to schedule.',
  },
  {
    title: 'General Contracting',
    description:
      'Manage all subcontractors, procurement processes, and construction phases to deliver the project efficiently and on budget.',
  },
  {
    title: 'Design Management',
    description:
      'Collaborate closely with architects and engineers to ensure the design aligns with client objectives and complies with regulations.',
  },
  {
    title: 'Tender Management',
    description:
      'Tender document preparation, evaluation, & submission processes to secure contracts and manage bid strategy.',
  },
  {
    title: 'Client Representation',
    description:
      'Act as the client’s representative on-site to ensure project requirements and expectations are fully met throughout the lifecycle.',
  },
  {
    title: 'Consultancy Services',
    description:
      'Provide expert guidance and strategic insights to support decision-making and project success at every stage.',
  },
  {
    title: 'Structural Steel Fabrication & Assembly',
    description:
      'Fabricate and assemble steel structures, adhering to engineering standards and quality control requirements.',
  },
  {
    title: 'Value Engineering / Design Review',
    description:
      'Review and optimise designs to maximise functionality and reduce costs without compromising on quality or safety.',
  },
  {
    title: 'Cost Analysis and Estimation',
    description:
      'Perform detailed cost evaluations and estimations to ensure accurate budgeting and effective financial control.',
  },
  {
    title: 'Procurement',
    description:
      'Source, negotiate, and manage materials, equipment, and services to meet project requirements efficiently.',
  },
  {
    title: 'Claims Management',
    description:
      'Manage contractual claims professionally to minimise disputes, delays, and financial impacts on the project.',
  },
  {
    title: 'Troubleshooting',
    description:
      'Quickly identify and resolve technical or operational issues to maintain project progress and minimise downtime.',
  },
  {
    title: 'Insulation Services',
    description:
      'Install insulation solutions to enhance energy efficiency, thermal protection, and safety across various structures.',
  },
  {
    title: 'Welding, Fabrication and Maintenance',
    description:
      'Provide expert welding, metal fabrication, and maintenance services to support ongoing project and operational needs.',
  },
  {
    title: 'Fabrication and Installation of Piping',
    description:
      'Custom fabricate and install piping systems ensuring compliance with specifications and industry standards.',
  },
  {
    title: 'Fabrication and Construction of Tanks',
    description:
      'Construct and assemble tanks with precision, following engineering requirements and quality assurance protocols.',
  },
  {
    title: 'Abrasive Blasting and Coating',
    description:
      'Prepare surfaces using abrasive blasting and apply protective coatings to improve durability and corrosion resistance.',
  },
  {
    title: 'Scaffolding Services',
    description:
      'Design, erect, and dismantle scaffolding systems safely to support construction activities at all project stages.',
  },
  {
    title: 'Landscaping and Property Maintenance',
    description:
      'Design and maintain outdoor spaces, enhancing aesthetic appeal and functional use of properties.',
  },
  {
    title: 'Project Turn Around Services',
    description:
      'Support shutdowns, turnarounds, and restarts with efficient planning and management to minimise downtime.',
  },
  {
    title: 'Lift Plans, Equipment Rentals & Transportation',
    description:
      'Coordinate lift planning, equipment rentals, and transport logistics to ensure smooth project operations.',
  },
];

/* ------------------------------------------------------------------ */
/* Service detail pages                                                */
/* ------------------------------------------------------------------ */

export const residentialCommercial = {
  eyebrow: 'Services',
  title: 'Residential & Commercial',
  lead: 'Building attractive, affordable, energy efficient housing on time and within budget.',
  image: require('../images/s-3.jpeg'),
  heroImage: require('../images/main-slider/slider2/slide3.jpg'),
  paragraphs: [
    'We provide homes that are well-built, aesthetically pleasing, and economically viable. A mass housing builder with a proven record of success in the affordable housing construction market. We work closely with private / non-profit organizations and take a very personal approach to each project.',
    'Building responsibly means being client centred, quality focused and budget driven. Our construction management methodology is aimed at exceeding our client’s expectations on every project.',
    'We also take seriously our responsibility to be good stewards of the environment through sustainable building practices, conscientious recycling and being strong advocates for energy efficient alternatives whenever and wherever possible.',
  ],
};

export const industrialMechanical = {
  eyebrow: 'Services',
  title: 'Industrial & Mechanical',
  lead:
    'Providing specialized expertise in design, analysing, and optimizing mechanical systems, processes, and products.',
  image: require('../images/s-2.jpeg'),
  heroImage: require('../images/main-slider/slider2/slide2.jpg'),
  disciplines: [
    {
      title: 'Value Engineering',
      body:
        'Review designs and make recommendations to achieve optimum value by evaluating design options, analysing all aspects of design including systems and materials to make it more efficient and less costly.',
    },
    {
      title: 'Cost Analysis',
      body:
        'Evaluation of the price of a specific project. Can be done at budgetary level, prior to completion of the design based on the assumed performance specifications, at an accurate level, once a full design is complete.',
    },
    {
      title: 'Chemical Engineering',
      body:
        'Providing expert advice, analysis and solutions in the field of chemical processes, production, and technology. Our knowledge on chemistry, engineering, and industrial systems, will help clients optimize operations.',
    },
    {
      title: 'Mechanical Engineering',
      body:
        'Providing specialized expertise in design, analysing, and optimizing mechanical systems, processes, and products. We advise on design development, thermal and fluid dynamics, equipment selection and specifications.',
    },
    {
      title: 'Civil Engineering',
      body:
        'Expert guidance in planning, design, construction, and maintenance of infrastructure projects. Providing Feasibility studies, site analysis and surveying, urban and reginal planning, environmental engineering.',
    },
    {
      title: 'Structural Engineering',
      body:
        'Providing expert advice, analysis, and design solutions to ensure that building and infrastructure are safe, stable, and efficient. Evaluating of structures to withstand various key forces and environmental conditions.',
    },
    {
      title: 'HSE Consultancy',
      body:
        'Providing specialized guidance to organizations to ensure compliance with health, safety and environmental regulations, promote workplace safety, reducing environmental impact.',
    },
    {
      title: 'Procurement',
      body:
        'We survey the market, recommend vendors and sub-contractors, issue Requests for Quotations, analyse offers, and make recommendations to our client.',
    },
    {
      title: 'Claims Management',
      body:
        'Reviewal and response to claims submitted against client ensuring his best position contractually. Additionally. We can prepare claims on behalf of a client.',
    },
    {
      title: 'Troubleshooting',
      body:
        'Analyse and recommend for specific project concerns such as, project organization review, issues related to costs, quality, progress, claims, or even project documentation.',
    },
  ],
};

export const managementServices = {
  eyebrow: 'Services',
  title: 'Management Services',
  lead: 'We manage various phases of projects as an extension of the owner’s organization.',
  image: require('../images/s-1.png'),
  heroImage: require('../images/main-slider/slider2/slide4.jpg'),
  paragraphs: [
    'Depending on the project requirements, we establish the procedure for the appointment of an architect / engineer to produce designs and a main contractor to carry out construction. We set strategy and manage the activities of the architect, engineers and main contractors covering all phases from pre-construction through completion and act as the single point of contact with the owner.',
  ],
  groups: [
    {
      title: 'Construction Management',
      items: [
        { term: 'Project Planning', detail: 'Developing timelines, budgets, and resource allocation strategies.' },
        { term: 'Design Coordination', detail: 'Collaborating with architects and engineers to meet specifications.' },
        { term: 'Budget & Cost Management', detail: 'Monitoring expenses and controlling costs.' },
        { term: 'Resource Procurement', detail: 'Sourcing materials, equipment, and labour.' },
        { term: 'Risk Management', detail: 'Identifying risks and implementing mitigation strategies.' },
        { term: 'Quality Assurance', detail: 'Ensuring work meets industry standards and expectations.' },
        { term: 'On-Site Supervision', detail: 'Overseeing daily operations and managing sub-contractors.' },
        { term: 'Safety Compliance', detail: 'Implementing safety protocols to protect workers and public.' },
        { term: 'Project Closeout', detail: 'Completing final inspections and ensuring deliverables are met.' },
      ],
    },
    {
      title: 'Design Management',
      items: [
        { term: 'Client Consultation', detail: 'Understanding vision, needs, and objectives.' },
        { term: 'Design Integration', detail: 'Collaborating to develop cohesive and practical designs.' },
        { term: 'Feasibility Studies', detail: 'Assessing practicality in terms of cost and regulations.' },
        { term: 'Value Engineering', detail: 'Optimizing designs to maximize functionality and minimize costs.' },
        { term: 'Regulatory Compliance', detail: 'Ensuring adherence to building codes and safety standards.' },
        { term: 'Coordination Across Phases', detail: 'Managing transition from design to construction.' },
        { term: 'Quality Assurance', detail: 'Reviewing design outputs for accuracy and efficiency.' },
        { term: 'Innovation & Sustainability', detail: 'Incorporating modern technologies and sustainable practices.' },
      ],
    },
    {
      title: 'Tender Management',
      items: [
        { term: 'Tender Analysis', detail: 'Reviewing tender documents and requirements.' },
        { term: 'Feasibility Assessment', detail: 'Evaluating project viability for bidding.' },
        { term: 'Cost Estimations', detail: 'Preparing detailed cost breakdowns and pricing strategies.' },
        { term: 'Documentation Preparation', detail: 'Compiling proposals, financial details, and certifications.' },
        { term: 'Subcontractor Coordination', detail: 'Obtaining accurate quotations from suppliers.' },
        { term: 'Risk Management', detail: 'Identifying risks and proposing mitigation strategies.' },
        { term: 'Submission & Follow-up', detail: 'Ensuring timely submission and communication.' },
        { term: 'Post Tender', detail: 'Engaging in negotiations and refining terms.' },
        { term: 'Compliance Assurance', detail: 'Ensuring bids meet legal and regulatory requirements.' },
      ],
    },
  ],
};

export const nav = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/#about' },
  {
    label: 'Services',
    to: '/#services',
    children: services.map((s) => ({ label: s.title, to: s.link })),
  },
  { label: 'Contact', to: '/#contact' },
];
